import {ApprovalStatus, Approver} from '@domain/shared'
import {useApproval} from '@domain/shifthandover'
import {useRequiredUser} from '@hconnect/login'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import {LoadingButton} from '@mui/lab'
import {Alert, Grid} from '@mui/material'
import {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_APPROVERS_WIDGET_NAMESPACE, STATUS_ALERT_PROPS} from '../consts'

type ApproversBannerProps = {
  approvers?: Approver[]
  eventId: string
  plantId: string
  onStatusChange: (status: ApprovalStatus) => void
  pendingAction?: ApprovalStatus
  isLoading: boolean
}

export const ApproversBanner: FC<ApproversBannerProps> = ({
  approvers,
  onStatusChange,
  pendingAction,
  isLoading
}) => {
  const {t} = useTranslation(COMMON_APPROVERS_WIDGET_NAMESPACE)
  const user = useRequiredUser()

  const approver = approvers?.find((approver) => approver.user.id === user.id)
  const isApproved = approver?.status === 'approved'
  const isPending = approver?.status === 'pending'

  const label = isPending
    ? t('approvers.label.nominatedAsApprover')
    : isApproved
      ? t('approvers.label.approvedByYou')
      : t('approvers.label.rejectedByYou')

  return approver ? (
    <Grid item xs={12}>
      <Alert
        data-test-id="event-approver-banner"
        icon={!isPending ? STATUS_ALERT_PROPS[approver.status].getIcon(20) : false}
        severity={STATUS_ALERT_PROPS[approver.status].severity}
        sx={{
          alignItems: 'center',
          '& .MuiAlert-action': {
            pt: 0,
            gap: 0.5
          }
        }}
        action={
          isPending ? (
            <>
              <LoadingButton
                onClick={() => onStatusChange('rejected')}
                variant="text"
                startIcon={<CancelOutlinedIcon />}
                loading={isLoading && pendingAction === 'rejected'}
                disabled={isLoading && pendingAction === 'approved'}
                data-test-id="event-approver-banner-reject"
              >
                {t('approvers.action.reject')}
              </LoadingButton>
              <LoadingButton
                onClick={() => onStatusChange('approved')}
                variant="contained"
                startIcon={<CheckCircleOutlineOutlinedIcon />}
                loading={isLoading && pendingAction === 'approved'}
                disabled={isLoading && pendingAction === 'rejected'}
                data-test-id="event-approver-banner-approve"
              >
                {t('approvers.action.approve')}
              </LoadingButton>
            </>
          ) : undefined
        }
      >
        {label}
      </Alert>
    </Grid>
  ) : null
}
